import { useCallback, useState } from 'react';
import { GameLaunchMode } from '../constants/gameLaunchMode';
import type { Game } from '../services/games';
import { trackGameClosed, trackGameLaunched } from '../helpers/analytics';
import {
    detectClientLaunchFormat,
    type ClientLaunchFormat,
} from '../helpers/detectClientLaunchFormat';
import {
    buildPlayPageUrl,
    gameClientPath,
    getGameLaunchMode,
    setAtemoyaCheatsAllowed,
    withGameLaunchParams,
    withLauncherContext,
} from '../helpers/gameLaunch';
import {
    openLegacyGameInNewTab,
    openLegacyGameInSameTab,
} from '../helpers/legacyIframeLaunch';
import { launcherPreferencesService } from '../services/launcherPreferences';

type ActiveGame = {
    url: string;
    name: string;
    format: ClientLaunchFormat;
};

type UseGameLaunchOptions = {
    returnUrl?: string;
    onAfterClose?: () => void;
    useGameLocale?: boolean;
};

export function useGameLaunch(options: UseGameLaunchOptions = {}) {
    const { returnUrl, onAfterClose, useGameLocale = false } = options;
    const [activeGame, setActiveGame] = useState<ActiveGame | null>(null);

    const handleLaunch = useCallback(
        async (game: Game) => {
            if (game.comingSoon) {
                return;
            }

            const launchMode = getGameLaunchMode();
            const locale = useGameLocale
                ? launcherPreferencesService.getGameLocale()
                : launcherPreferencesService.getLocale();
            const currency = launcherPreferencesService.getCurrency();
            const cheatsAllowed = Boolean(game.cheatsAllowed);
            setAtemoyaCheatsAllowed(cheatsAllowed);
            const gameUrl = withLauncherContext(
                withGameLaunchParams(gameClientPath(game.id, 'index.html'), {
                    locale,
                    currency,
                    cheatsEnabled: cheatsAllowed,
                }),
                launchMode,
                returnUrl
            );

            let format: ClientLaunchFormat;
            try {
                format = await detectClientLaunchFormat(gameUrl);
            } catch (error) {
                console.error('[useGameLaunch] Failed to detect launch format:', error);
                // Prefer legacy iframe for unrebuilt / unknown dists so the demo stays usable.
                format = 'legacy';
            }

            trackGameLaunched({
                game_name: game.name,
                game_url: gameUrl,
                launch_mode: launchMode,
            });

            if (launchMode === GameLaunchMode.NewTab) {
                if (format === 'canonical') {
                    const playUrl = buildPlayPageUrl(gameUrl, game.name);
                    window.open(playUrl, '_blank', 'noopener,noreferrer');
                } else {
                    openLegacyGameInNewTab(gameUrl);
                }
                return;
            }

            if (launchMode === GameLaunchMode.SameTab) {
                if (format === 'canonical') {
                    const playUrl = buildPlayPageUrl(gameUrl, game.name);
                    window.location.assign(playUrl);
                } else {
                    openLegacyGameInSameTab(gameUrl);
                }
                return;
            }

            setActiveGame({ url: gameUrl, name: game.name, format });
        },
        [returnUrl, useGameLocale]
    );

    const handleClose = useCallback(() => {
        if (!activeGame) {
            return;
        }
        trackGameClosed(activeGame.name);
        setActiveGame(null);
        onAfterClose?.();
    }, [activeGame, onAfterClose]);

    return { handleLaunch, activeGame, handleClose };
}
